/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { ReactNode } from 'react';

import { css, jsx } from '@compiled/react';

import {
	BrandHeading,
	Card,
	type GroupData,
	type IconCardData,
	IconCardGroup,
	Image,
} from '@af/design-system-docs-ui';
import Heading from '@atlaskit/heading';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';

import OverviewPage from '../../templates/overview-page';

import components from './_assets/components.png';
import Content from './_assets/content.mdx';
import foundations from './_assets/foundations.png';
import getstarted from './_assets/get-started.png';
import heroDark from './_assets/hero.dark.png';
import heroLight from './_assets/hero.light.png';
import tokens from './_assets/tokens.png';

const gridOverrideStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	div: {
		padding: '0',
	},
});

const cardContentStyles = xcss({
	padding: 'space.100',
});

const guidanceCards = [
	{
		image: getstarted,
		title: 'Get started',
		description:
			'Start designing and developing beautiful interfaces with Atlassian Design System foundations, components, and standards.',
		href: '/get-started',
	},
	{
		image: foundations,
		title: 'Foundations',
		description:
			'Foundations create engaging user experiences. These include our tokens, guidelines, and visual styles: color, spacing, typography, and more.',
		href: '/foundations',
	},
	{
		image: components,
		title: 'Components',
		description:
			'Components are reusable building blocks that meet specific interaction needs. They combine to create intuitive user experiences.',
		href: '/components',
	},
	{
		image: tokens,
		title: 'Tokens',
		description:
			'Design tokens are the single source of truth to name and store decisions about the user interface, such as colors, typography, and spacing.',
		href: '/foundations/tokens',
	},
];

const externalLinkCards: GroupData<IconCardData>[] = [
	{
		title: 'Ask a question or book a critique',
		cards: [
			{
				href: 'https://community.developer.atlassian.com/',
				title: 'Visit developer community (public only)',
				description: 'Search the Atlassian Developer Community forum and post questions.',
			},
			{
				href: 'https://go.atlassian.com/help-design-system',
				title: 'Slack a question (Atlassians only)',
				description: 'Reach out to our team in our slack channel.',
			},
			{
				href: 'https://go.atlassian.com/ads-office-hours',
				title: 'Book a design critique (Atlassians only)',
				description: 'Request a crit session to get feedback on your designs.',
			},
		],
	},
];

export default () => {
	return (
		<OverviewPage
			categorySlug="contact-us"
			title="Contact us"
			description="How to get help from the Atlassian Design System team and contribute to the system."
			entries={[]}
			image={{
				light: heroLight,
				dark: heroDark,
			}}
		>
			<Stack space="space.800">
				<section>
					<Stack space="space.200">
						<BrandHeading as="h2" size="large">
							Check our guidance first
						</BrandHeading>
						<Text>Before you reach out, look for an answer in these places.</Text>
						<Stack space="space.100">
							{guidanceCards.map((card) => (
								<Card borderColor="color.border" href={card.href}>
									<CardGrid>
										<Box backgroundColor="elevation.surface.sunken">
											<Image src={card.image} alt={card.title} />
										</Box>

										<Stack xcss={cardContentStyles} space="space.100" alignBlock="center">
											<Heading size="small">{card.title}</Heading>
											<Text>{card.description}</Text>
										</Stack>
									</CardGrid>
								</Card>
							))}
						</Stack>
					</Stack>
				</section>
				<section>
					<Stack space="space.200">
						<IconCardGroup data={externalLinkCards} />
						<Text as="p">
							Empowering you to ship value to your customers is a priority. To avoid being a
							bottleneck, we'd love to be involved early in your app implementation through our
							participation channels.
						</Text>
						<Text as="p">
							If you need to add something or believe something is missing, post a question in slack
							so that we can offer support and connections.
						</Text>
					</Stack>
				</section>
				<section css={gridOverrideStyles}>
					<Content />
				</section>
			</Stack>
		</OverviewPage>
	);
};

const cardGridStyles = xcss({
	display: 'grid',
	gap: 'space.100',
	gridTemplateColumns: '1fr',
	'@media (min-width: 30rem)': {
		gridTemplateColumns: '1fr 1fr',
	},
});

const CardGrid = ({ children }: { children: ReactNode }) => (
	<Box xcss={cardGridStyles}>{children}</Box>
);
